import { useCallback } from 'react';

import { useTrackSignupPhoneFieldViewed } from '@/hooks/useTrackSignupPhoneFieldViewed';
import { type ClinicianTypeInput } from '@/lib/types';
import {
  ErrorSeverity,
  ErrorType,
  OnboardingErrorStep,
} from '@/models/analytics/features/errors';
import { FieldType } from '@heidi/runtime/analytics-vocabulary';
import { type OrganisationRole } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';

import {
  useOnboardingFieldTracking,
  useTrackOnboardingErrorImpression,
} from '@/models/analytics/features/useOnboardingFieldTracking';

/**
 * Encapsulates analytics for `AboutYouStep`: field-selected events for the
 * specialty/role/country/jurisdiction dropdowns plus the country/phone
 * mismatch warning impression. Keeps the step file focused on UI.
 *
 * Callers receive lazy wrappers that fire the analytics event then forward
 * the value to the upstream `on*Change` callback unchanged.
 */
export function useAboutYouAnalytics(params: {
  country: CountryType | null;
  hasCountryPhoneMismatch: boolean;
  isPhoneFieldVisible: boolean;
  isPhoneNumberOptional: boolean;
  onSpecialtyChange: (value: ClinicianTypeInput | null) => void;
  onRoleChange: (value: OrganisationRole | null) => void;
  onCountryChange: (value: CountryType | null) => void;
  onJurisdictionChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
}) {
  const {
    country,
    hasCountryPhoneMismatch,
    isPhoneFieldVisible,
    isPhoneNumberOptional,
    onSpecialtyChange,
    onRoleChange,
    onCountryChange,
    onJurisdictionChange,
    onPhoneNumberChange,
  } = params;
  const { trackImmediate, trackTextDebounced } = useOnboardingFieldTracking('about_you');

  useTrackSignupPhoneFieldViewed({
    isVisible: isPhoneFieldVisible,
    isOptional: isPhoneNumberOptional,
    surface: 'onboarding_about_you_step',
    countryCode: country?.countryCode,
  });

  useTrackOnboardingErrorImpression(hasCountryPhoneMismatch, {
    step: OnboardingErrorStep.ABOUT_YOU,
    errorType: ErrorType.VALIDATION_ERROR,
    errorCode: 'country_phone_mismatch',
    errorMessage: 'Country/phone number mismatch',
    severity: ErrorSeverity.INFO,
    country: country?.countryCode,
  });

  const handleSpecialtyChange = useCallback(
    (value: ClinicianTypeInput | null | undefined) => {
      // Capture the chosen specialty (bounded, non-PII clinician-type name —
      // e.g. "Acupuncturist") as `field_value` on the field-selected event.
      if (value) trackImmediate('specialty', FieldType.Dropdown, value.name);
      onSpecialtyChange(value ?? null);
    },
    [trackImmediate, onSpecialtyChange]
  );

  const handleRoleChange = useCallback(
    (value: OrganisationRole) => {
      trackImmediate('role', FieldType.Dropdown);
      onRoleChange(value);
    },
    [trackImmediate, onRoleChange]
  );

  const handleCountryChange = useCallback(
    (value: CountryType | null) => {
      if (value) trackImmediate('country', FieldType.Dropdown);
      onCountryChange(value);
    },
    [trackImmediate, onCountryChange]
  );

  const handleJurisdictionChange = useCallback(
    (value: string) => {
      // Empty value = auto-detection from country, not user input.
      if (value) trackImmediate('jurisdiction', FieldType.Dropdown);
      onJurisdictionChange(value);
    },
    [trackImmediate, onJurisdictionChange]
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      // Debounced (text field) — one field_selected per pause, not per keystroke.
      trackTextDebounced('phone_number');
      onPhoneNumberChange(value);
    },
    [trackTextDebounced, onPhoneNumberChange]
  );

  return {
    handleSpecialtyChange,
    handleRoleChange,
    handleCountryChange,
    handleJurisdictionChange,
    handlePhoneNumberChange,
  };
}
